// © 2026 Camp Promethean, LLC. All Rights Reserved.
// User-facing labels for Prometheus — hide internal tool names from the UI.

/** Starter questions a grower would actually ask at standup. */
export const STARTER_PROMPTS = [
  "Which rooms drifted on runoff EC this week?",
  "What's our IPM protocol for russet mites in flower?",
  "Which strains came in light last harvest?",
  "Any rooms that missed irrigation logs this week?",
];

/** Page-aware starters for the floating assistant. */
export function contextStarterPrompts(recordType: string): string[] {
  switch (recordType) {
    case "crop":
      return [
        "How did this crop's runoff look last week?",
        "Any open work left on this crop?",
      ];
    case "batch":
      return [
        "How did this batch yield against target?",
        "Any quality notes on this batch?",
      ];
    case "breeding project":
    case "trait project":
      return [
        "Which phenos are ahead in this project?",
        "Any selections that fell behind last week?",
      ];
    default:
      return [
        `Any issues on this ${recordType} this week?`,
        `What's still open on this ${recordType}?`,
      ];
  }
}

const TOOL_STATUS_LABELS: Record<string, string> = {
  get_room_environment: "Checking room conditions",
  compare_crop_substrate: "Comparing crop cycles",
  get_steering_context: "Checking cultivation guidance",
  read_dataset: "Reading more records",
  create_cultivation_report: "Preparing your report",
  query_data: "Checking your records",
  search_context: "Searching your docs",
  make_chart: "Building a chart",
  generate_report: "Drafting a report",
};

/** Map backend tool names to grower-friendly status text. */
export function friendlyToolLabel(name: string): string {
  return TOOL_STATUS_LABELS[name] ?? "Looking into it";
}

/** Dedupe tool labels for a single status line. */
export function formatToolStatus(toolNames: string[]): string | null {
  if (toolNames.length === 0) return null;
  const labels = [...new Set(toolNames.map(friendlyToolLabel))];
  return labels.join(" · ");
}
